import { useState } from "react";
import axios from "axios";
import { AlertCircle, CheckCircle2, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import TournamentAppearanceEditor from "./TournamentAppearanceEditor";

const requestKey = () =>
  `division_${globalThis.crypto?.randomUUID?.().replaceAll("-", "") || `${Date.now()}_${Math.random().toString(36).slice(2)}`}`;

const TournamentOperationsWorkspace = ({
  workspace,
  metadata,
  headers,
  onBack,
  onRefresh,
}) => {
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("divisions");
  const [actionError, setActionError] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [scores, setScores] = useState({});
  const [formatSelections, setFormatSelections] = useState({});
  const [venue, setVenue] = useState({ name: "", resource_name: "", address: "" });
  const [editingDetails, setEditingDetails] = useState(false);
  const [details, setDetails] = useState(() => ({
    name: workspace.tournament.name,
    sport: workspace.tournament.sport,
    participant_type: workspace.tournament.participant_type,
    expected_participants: workspace.tournament.expected_participants,
    pool_size: workspace.tournament.pool_size || 4,
    starts_on: workspace.tournament.starts_on,
    ends_on: workspace.tournament.ends_on,
    timezone: workspace.tournament.timezone,
    payment_mode: workspace.tournament.payment_mode,
    payment_timing: workspace.tournament.payment_timing || "after_approval",
    location: workspace.tournament.location,
    registration_closes_on: workspace.tournament.registration_closes_on,
  }));
  const [division, setDivision] = useState({
    idempotency_key: requestKey(),
    name: "",
    classification: "Open",
    competitive_level: "open",
    gender: "open",
    capacity: 16,
    entry_fee_cents: 0,
    format_template: "pool_to_bracket",
    pool_size: 4,
    required_documents: "",
    eligibility_notes: "",
  });

  const saveDivision = async (event) => {
    event.preventDefault();
    setSaving(true);
    setActionError("");
    try {
      await axios.post(
        `${API}/modules/tournaments/${workspace.tournament.tournament_id}/divisions`,
        {
          ...division,
          capacity: Number(division.capacity),
          entry_fee_cents: Number(division.entry_fee_cents),
          pool_size: Number(division.pool_size),
          required_documents: division.required_documents
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
        },
        { headers },
      );
      setDivision((current) => ({
        ...current,
        idempotency_key: requestKey(),
        name: "",
        required_documents: "",
        eligibility_notes: "",
      }));
      await onRefresh();
      toast.success("Division added");
    } catch (error) {
      const message = error.response?.data?.detail?.[0]?.msg ||
        error.response?.data?.detail || "Division could not be added";
      setActionError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = async (event) => {
    event.preventDefault();
    setPendingAction("save-details");
    setActionError("");
    try {
      await axios.patch(`${API}/modules/tournaments/${workspace.tournament.tournament_id}`, {
        ...details,
        expected_participants: Number(details.expected_participants),
        pool_size: Number(details.pool_size),
      }, { headers });
      setEditingDetails(false);
      await onRefresh();
      toast.success("Tournament details updated");
    } catch (error) {
      const message = error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || "Tournament details could not be updated";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const decide = async (application, decision) => {
    setPendingAction(`${application.application_id}:${decision}`);
    setActionError("");
    try {
      const response = await axios.patch(
        `${API}/modules/tournaments/${workspace.tournament.tournament_id}/applications/${application.application_id}`,
        { decision, note: "" },
        { headers },
      );
      await onRefresh();
      toast.success(
        response.data.capacity_reached
          ? "Division is full; application moved to waitlist"
          : `Application ${response.data.status}`,
      );
    } catch (error) {
      const message = error.response?.data?.detail || "Application could not be updated";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const openApplications = async () => {
    setPendingAction("open-applications");
    setActionError("");
    try {
      await axios.patch(
        `${API}/modules/tournaments/${workspace.tournament.tournament_id}/status`,
        { status: "applications_open" },
        { headers },
      );
      await onRefresh();
      toast.success("Tournament applications opened");
    } catch (error) {
      const message = error.response?.data?.detail?.message ||
        error.response?.data?.detail || "Applications could not be opened";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const generateCompetition = async (divisionId) => {
    setPendingAction(`generate:${divisionId}`);
    setActionError("");
    try {
      await axios.post(
        `${API}/modules/tournaments/${workspace.tournament.tournament_id}/competitions`,
        { division_id: divisionId, idempotency_key: requestKey() },
        { headers },
      );
      await onRefresh();
      toast.success("Competition generated");
    } catch (error) {
      const message = error.response?.data?.detail || "Competition could not be generated";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const saveFormat = async (divisionItem) => {
    const format = formatSelections[divisionItem.division_id] || divisionItem.format_template || "pool_to_bracket";
    setPendingAction(`format:${divisionItem.division_id}`);
    setActionError("");
    try {
      await axios.patch(
        `${API}/modules/tournaments/${workspace.tournament.tournament_id}/divisions/${divisionItem.division_id}/format`,
        { format_template: format, pool_size: 4 },
        { headers },
      );
      await onRefresh();
      toast.success("Competition format saved");
    } catch (error) {
      const message = error.response?.data?.detail || "Competition format could not be saved";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const saveVenue = async (event) => {
    event.preventDefault();
    setPendingAction("venue");
    setActionError("");
    try {
      await axios.post(`${API}/modules/tournaments/${workspace.tournament.tournament_id}/venues`, { ...venue, idempotency_key: requestKey() }, { headers });
      setVenue({ name: "", resource_name: "", address: "" });
      await onRefresh();
      toast.success("Field added");
    } catch (error) {
      const message = error.response?.data?.detail || "Field could not be added";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const submitScore = async (match) => {
    const score = scores[match.match_id] || {};
    const correctionReason =
      match.status === "final"
        ? window.prompt("Why is this final score being corrected?")
        : "";
    if (match.status === "final" && !correctionReason) return;
    setPendingAction(`score:${match.match_id}`);
    setActionError("");
    try {
      await axios.post(
        `${API}/modules/tournaments/${workspace.tournament.tournament_id}/matches/${match.match_id}/score`,
        {
          home_score: Number(score.home_score),
          away_score: Number(score.away_score),
          correction_reason: correctionReason || "",
        },
        { headers },
      );
      await onRefresh();
      toast.success("Score recorded");
    } catch (error) {
      const message = error.response?.data?.detail || "Score could not be recorded";
      setActionError(message);
      toast.error(message);
    } finally {
      setPendingAction("");
    }
  };

  const nextStep = workspace.divisions.length === 0
      ? { tab: "divisions", label: "Add the first registration category" }
    : workspace.tournament.status === "draft"
      ? { tab: "entries", label: "Open registration" }
      : workspace.applications.length === 0
        ? { tab: "entries", label: "Share the application page" }
        : (workspace.matches || []).length === 0
          ? { tab: "formats", label: "Set formats after entries are approved" }
          : { tab: "scoring", label: "Run matches and record scores" };

  const tabs = [
    { id: "appearance", label: "Appearance" },
    { id: "divisions", label: "Divisions", count: workspace.divisions.length },
    { id: "entries", label: "Entries", count: workspace.applications.length },
    { id: "formats", label: "Formats", count: workspace.divisions.filter((item) => item.format_plan).length },
    { id: "fields", label: "Fields", count: workspace.venues?.length || 0 },
    { id: "schedule", label: "Schedule", count: (workspace.matches || []).filter((item) => item.starts_at).length },
    { id: "communications", label: "Communications", count: 0 },
    { id: "scoring", label: "Scoring & advancement", count: (workspace.matches || []).filter((item) => item.status === "final").length },
    { id: "launch", label: "Launch", count: workspace.readiness.ready_to_open ? 1 : 0 },
  ];
  const structureLocked = workspace.tournament.status !== "draft";

  return (
    <section
      className="space-y-6"
      data-testid="tournament-operations-workspace"
    >
      <div className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
        <button
          type="button"
          onClick={onBack}
          className="mb-5 flex items-center gap-1 text-sm font-semibold text-[#087f67]"
        >
          <ChevronLeft className="h-4 w-4" /> All tournaments
        </button>
        <div className="grid gap-6 lg:grid-cols-[1fr_280px] lg:items-start">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">
              Tournament setup
            </div>
            <h2 className="mt-2 text-3xl font-semibold text-[#102a27]">
              {workspace.tournament.name}
            </h2>
            <span className="mt-3 inline-flex rounded-full bg-[#eef8f3] px-3 py-1 text-xs font-semibold capitalize text-[#087f67]">
              {(workspace.tournament.status || "draft").replaceAll("_", " ")}
            </span>
            <p className="mt-2 text-sm text-[#60706a]">
              {metadata?.sports?.[workspace.tournament.sport]?.label} ·{" "}
              {workspace.tournament.location || "Location to be confirmed"} · {workspace.tournament.starts_on}–{workspace.tournament.ends_on}
            </p>
            <Button type="button" variant="outline" onClick={() => setEditingDetails((current) => !current)} className="mt-4 rounded-xl">{editingDetails ? "Close details" : "Edit tournament details"}</Button>
            {editingDetails && <form onSubmit={saveDetails} className="mt-5 grid gap-4 rounded-[18px] border border-[#dfe6df] bg-[#fbfcf8] p-4 sm:grid-cols-2">
              {structureLocked && <p className="rounded-xl border border-[#ead9a6] bg-[#fff9e8] p-3 text-sm text-[#725a1d] sm:col-span-2">Registration is open, so competition structure and dates are locked. You can still update the tournament name and location.</p>}
              <label className="grid gap-2 text-sm font-semibold text-[#29443f] sm:col-span-2">Tournament name<Input required minLength={3} value={details.name} onChange={(event) => setDetails((current) => ({ ...current, name: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Sport<select disabled={structureLocked} value={details.sport} onChange={(event) => setDetails((current) => ({ ...current, sport: event.target.value }))} className="h-10 rounded-md border bg-white px-3 disabled:bg-[#edf1ee]">{Object.entries(metadata?.sports || {}).map(([key, sport]) => <option key={key} value={key}>{sport.label}</option>)}</select></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Participant type<select disabled={structureLocked} value={details.participant_type} onChange={(event) => setDetails((current) => ({ ...current, participant_type: event.target.value }))} className="h-10 rounded-md border bg-white px-3 disabled:bg-[#edf1ee]"><option value="team">Teams</option><option value="individual">Individuals</option><option value="pair">Pairs</option></select></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Expected entries<Input disabled={structureLocked} required type="number" min="2" max="1024" value={details.expected_participants} onChange={(event) => setDetails((current) => ({ ...current, expected_participants: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Starts<Input disabled={structureLocked} required type="date" value={details.starts_on} onChange={(event) => setDetails((current) => ({ ...current, starts_on: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Ends<Input disabled={structureLocked} required type="date" min={details.starts_on} value={details.ends_on} onChange={(event) => setDetails((current) => ({ ...current, ends_on: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Location<Input required value={details.location || ""} onChange={(event) => setDetails((current) => ({ ...current, location: event.target.value }))} /></label>
              <label className="grid gap-2 text-sm font-semibold text-[#29443f]">Registration closes<Input disabled={structureLocked} required type="date" max={details.starts_on} value={details.registration_closes_on || ""} onChange={(event) => setDetails((current) => ({ ...current, registration_closes_on: event.target.value }))} /></label>
              {details.payment_mode === "delegated" && <label className="grid gap-2 text-sm font-semibold text-[#29443f] sm:col-span-2">Payment timing<select disabled={structureLocked} value={details.payment_timing} onChange={(event) => setDetails((current) => ({ ...current, payment_timing: event.target.value }))} className="h-10 rounded-md border bg-white px-3 disabled:bg-[#edf1ee]"><option value="after_approval">After organizer approval</option><option value="at_application">With a complete application</option></select></label>}
              <div className="flex flex-col-reverse gap-2 sm:col-span-2 sm:flex-row sm:justify-end"><Button type="button" variant="outline" onClick={() => setEditingDetails(false)}>Cancel</Button><Button type="submit" disabled={pendingAction === "save-details"}>{pendingAction === "save-details" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save details</Button></div>
            </form>}
            <div className="mt-5 rounded-[18px] border border-[#dfe6df] bg-[#fbfcf8] p-4">
              <div className="text-xs font-bold uppercase tracking-wide text-[#60706a]">
                Next action
              </div>
              <button
                type="button"
                onClick={() => setActiveTab(nextStep.tab)}
                className="mt-1 text-left font-semibold text-[#087f67] hover:underline"
              >
                {nextStep.label}
              </button>
            </div>
          </div>
          <div className="rounded-[20px] bg-[#eef8f3] p-5">
            <div className="text-xs font-bold uppercase tracking-wide text-[#087f67]">
              Readiness
            </div>
            <div className="mt-2 text-4xl font-semibold text-[#102a27]">
              {workspace.readiness.score}%
            </div>
            <div className="mt-3 space-y-2">
              {workspace.readiness.checks.map((check) => (
                <div
                  key={check.key}
                  className="flex items-start gap-2 text-xs text-[#52615b]"
                >
                  <CheckCircle2
                    className={`mt-0.5 h-4 w-4 shrink-0 ${check.complete ? "text-[#087f67]" : "text-[#b88713]"}`}
                  />
                  {check.label}
                </div>
              ))}
            </div>
            {workspace.tournament.status === "draft" && (
              <Button
                onClick={openApplications}
                disabled={!workspace.readiness.ready_to_open || pendingAction === "open-applications"}
                className="mt-4 w-full rounded-xl bg-[#102a27]"
              >
                {pendingAction === "open-applications" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Open applications
              </Button>
            )}
            {workspace.tournament.status === "applications_open" && (
              <a
                href={`/tournaments/${workspace.tournament.tournament_id}/apply`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 block rounded-xl bg-white px-3 py-2 text-center text-xs font-semibold text-[#087f67]"
              >
                Applications are open · View page
              </a>
            )}
            {["applications_open", "applications_closed", "published", "live", "completed"].includes(workspace.tournament.status) && (
              <a
                href={`/tournaments/${workspace.tournament.tournament_id}/live`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 block rounded-xl border border-white/20 px-3 py-2 text-center text-xs font-semibold text-white"
              >
                View public tournament center
              </a>
            )}
          </div>
        </div>
      </div>
      <nav
        aria-label="Tournament workspace"
        className="overflow-x-auto rounded-[20px] border border-[#dfe6df] bg-white p-2"
      >
        <div className="flex min-w-max gap-2" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${activeTab === tab.id ? "bg-[#102a27] text-white" : "text-[#52615b] hover:bg-[#f2f5f0]"}`}
            >
              {tab.label} <span className="ml-1 opacity-70">{tab.count}</span>
            </button>
          ))}
        </div>
      </nav>
      <div hidden={activeTab !== "appearance"}>
        <TournamentAppearanceEditor
          key={workspace.tournament.tournament_id}
          tournament={workspace.tournament}
          headers={headers}
          onRefresh={onRefresh}
        />
      </div>
      {actionError && (
        <div className="flex items-start gap-3 rounded-[18px] border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><span className="font-semibold">That action did not complete.</span> {actionError}</div>
        </div>
      )}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-6">
          {activeTab === "divisions" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">
                  Competition structure
                </div>
                <h3 className="mt-2 text-xl font-semibold text-[#102a27]">
                  Divisions
                </h3>
              </div>
              <span className="text-sm text-[#60706a]">
                {workspace.divisions.length} configured
              </span>
            </div>
            {workspace.divisions.length === 0 ? (
              <p className="mt-5 rounded-xl bg-[#fbfcf8] p-5 text-sm text-[#60706a]">
                Add the first division to define capacity, eligibility,
                documents, and its competition format.
              </p>
            ) : (
              <div className="mt-5 grid gap-3">
                {workspace.divisions.map((item) => (
                  <article
                    key={item.division_id}
                    className="rounded-[18px] border border-[#e1e7e1] p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-[#102a27]">
                          {item.name}
                        </h4>
                        <p className="mt-1 text-xs text-[#60706a]">
                          {item.classification} · {item.gender || "open"} ·{" "}
                          {item.format_template ? metadata?.formats?.[item.format_template]?.name : "Format not set"}
                        </p>
                      </div>
                      <span className="rounded-full bg-[#eef8f3] px-2.5 py-1 text-xs font-semibold text-[#087f67]">
                        {item.accepted_count}/{item.capacity}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-[#60706a]">
                      {item.format_plan ? `${item.format_plan.estimated_matches} estimated matches · ${item.format_plan.minimum_guaranteed_matches} guaranteed` : "Collect registrations before choosing the competition format"}
                      {item.entry_fee_cents > 0 &&
                        ` · $${(item.entry_fee_cents / 100).toFixed(2)} entry`}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
          )}
          {activeTab === "formats" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">Competition design</div>
            <h3 className="mt-2 text-xl font-semibold text-[#102a27]">Set a format for each final division</h3>
            <p className="mt-2 text-sm text-[#60706a]">Choose formats after reviewing approved entries. Plans use the accepted count when available and remain editable until competition play begins.</p>
            <div className="mt-5 space-y-3">{workspace.divisions.map((item) => <div key={item.division_id} className="grid gap-3 rounded-[18px] border border-[#e1e7e1] p-4 sm:grid-cols-[1fr_minmax(220px,320px)_auto] sm:items-end"><div><div className="font-semibold text-[#102a27]">{item.name}</div><div className="text-xs text-[#60706a]">{item.accepted_count} approved · {item.capacity} capacity</div></div><label className="grid gap-1 text-xs font-semibold text-[#52615b]">Format<select aria-label={`${item.name} format`} value={formatSelections[item.division_id] || item.format_template || "pool_to_bracket"} onChange={(event) => setFormatSelections((current) => ({ ...current, [item.division_id]: event.target.value }))} className="h-10 rounded-md border bg-white px-3">{Object.entries(metadata?.formats || {}).map(([key, format]) => <option key={key} value={key}>{format.name}</option>)}</select></label><Button type="button" variant="outline" onClick={() => saveFormat(item)} disabled={Boolean(pendingAction)}>{pendingAction === `format:${item.division_id}` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save format</Button></div>)}</div>
          </section>
          )}
          {activeTab === "fields" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">Venues and resources</div>
            <h3 className="mt-2 text-xl font-semibold text-[#102a27]">Create the playable fields or courts</h3>
            <div className="mt-5 grid gap-3">{(workspace.venues || []).map((item) => <article key={item.venue_id} className="rounded-[18px] border border-[#e1e7e1] p-4"><div className="font-semibold text-[#102a27]">{item.resource_name}</div><div className="text-xs text-[#60706a]">{item.name}{item.address ? ` · ${item.address}` : ""}</div></article>)}</div>
            {(workspace.venues || []).length === 0 && <p className="mt-5 rounded-xl bg-[#fbfcf8] p-5 text-sm text-[#60706a]">No fields or courts have been created.</p>}
          </section>
          )}
          {activeTab === "schedule" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">Conflict-aware scheduling</div><h3 className="mt-2 text-xl font-semibold text-[#102a27]">Schedule generated matches</h3><p className="mt-2 text-sm text-[#60706a]">Generate each division from Formats, then assign its matches to the fields and times created here. Existing server checks prevent overlapping use of a field and overlapping games for the same entrant.</p><div className="mt-5 rounded-xl bg-[#fbfcf8] p-5 text-sm text-[#60706a]">{(workspace.matches || []).length ? `${workspace.matches.filter((item) => item.starts_at).length} of ${workspace.matches.filter((item) => item.status !== "bye").length} playable matches scheduled.` : "No matches are ready to schedule. Finalize formats and generate the competition first."}</div></section>
          )}
          {activeTab === "communications" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">Tournament communications</div><h3 className="mt-2 text-xl font-semibold text-[#102a27]">Messages follow the tournament lifecycle</h3><p className="mt-2 text-sm text-[#60706a]">Application decisions are sent from Entries. Schedule publication, schedule changes, scoring updates, and emergency broadcasts will use the organization communications service once their triggering data is ready.</p><div className="mt-5 grid gap-3 sm:grid-cols-2">{["Application decisions", "Payment and roster reminders", "Schedule published", "Schedule changes", "Results and advancement", "Emergency alerts"].map((label) => <div key={label} className="rounded-xl border border-[#e1e7e1] p-4 text-sm font-semibold text-[#29443f]">{label}</div>)}</div></section>
          )}
          {activeTab === "launch" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">Launch control</div><h3 className="mt-2 text-xl font-semibold text-[#102a27]">Registration and competition readiness</h3><div className="mt-5 space-y-3">{workspace.readiness.checks.map((check) => <div key={check.key} className="flex items-center gap-3 rounded-xl border border-[#e1e7e1] p-4 text-sm"><CheckCircle2 className={`h-5 w-5 ${check.complete ? "text-[#087f67]" : "text-[#b88713]"}`} /><span>{check.label}</span></div>)}</div></section>
          )}
          {activeTab === "entries" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
            <div className="flex items-end justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">
                  Team intake
                </div>
                <h3 className="mt-2 text-xl font-semibold text-[#102a27]">
                  Applications
                </h3>
              </div>
              <span className="text-sm text-[#60706a]">
                {workspace.applications.length} received
              </span>
            </div>
            {workspace.applications.length === 0 ? (
              <p className="mt-5 rounded-xl bg-[#fbfcf8] p-5 text-sm text-[#60706a]">
                {workspace.tournament.status === "applications_open"
                  ? "No entries yet. Share the public application page with teams and participants."
                  : "No entries yet. Complete setup and open applications when the tournament is ready."}
              </p>
            ) : (
              <div className="mt-5 space-y-3">
                {workspace.applications.map((item) => (
                  <article
                    key={item.application_id}
                    className="rounded-[18px] border border-[#e1e7e1] p-4"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className="font-semibold text-[#102a27]">
                          {item.participant_name}
                        </h4>
                        <p className="text-xs text-[#60706a]">
                          {item.club_name} · {item.contact_email} ·{" "}
                          {item.status}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => decide(item, "waitlisted")}
                          disabled={Boolean(pendingAction)}
                          className="rounded-lg"
                        >
                          Waitlist
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => decide(item, "accepted")}
                          disabled={Boolean(pendingAction)}
                          className="rounded-lg bg-[#102a27]"
                        >
                          Accept
                        </Button>
                      </div>
                    </div>
                    {item.missing_documents?.length > 0 && (
                      <p className="mt-3 text-xs text-[#8a5c11]">
                        Missing: {item.missing_documents.join(", ")}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
          )}
          {activeTab === "scoring" && (
          <section className="rounded-[24px] border border-[#dfe6df] bg-white p-5 sm:p-7">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">
              Live operations
            </div>
            <h3 className="mt-2 text-xl font-semibold text-[#102a27]">
              Competition and scores
            </h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {Object.entries(workspace.action_required || {}).map(
                ([key, count]) => (
                  <div key={key} className="rounded-xl bg-[#fbfcf8] p-3">
                    <div className="text-2xl font-semibold">{count}</div>
                    <div className="text-xs text-[#60706a]">
                      {key.replaceAll("_", " ")}
                    </div>
                  </div>
                ),
              )}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {workspace.divisions.map((item) => (
                <Button
                  key={item.division_id}
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => generateCompetition(item.division_id)}
                  disabled={Boolean(pendingAction)}
                >
                  {pendingAction === `generate:${item.division_id}` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Generate {item.name}
                </Button>
              ))}
            </div>
            <div className="mt-5 space-y-3">
              {(workspace.matches || []).filter((item) => item.status !== "bye").length === 0 && (
                <p className="rounded-xl bg-[#fbfcf8] p-5 text-sm text-[#60706a]">
                  No matches yet. Accept entries, then generate a competition for each ready division.
                </p>
              )}
              {(workspace.matches || [])
                .filter((item) => item.status !== "bye")
                .map((match) => (
                  <article
                    key={match.match_id}
                    className="grid gap-3 rounded-xl border border-[#e1e7e1] p-4 sm:grid-cols-[1fr_90px_90px_auto] sm:items-end"
                  >
                    <div className="text-sm font-semibold">
                      Round {match.round} · Match {match.sequence}
                    </div>
                    <Input
                      type="number"
                      min="0"
                      aria-label="Home score"
                      value={
                        scores[match.match_id]?.home_score ??
                        match.home_score ??
                        ""
                      }
                      onChange={(event) =>
                        setScores((current) => ({
                          ...current,
                          [match.match_id]: {
                            ...current[match.match_id],
                            home_score: event.target.value,
                          },
                        }))
                      }
                    />
                    <Input
                      type="number"
                      min="0"
                      aria-label="Away score"
                      value={
                        scores[match.match_id]?.away_score ??
                        match.away_score ??
                        ""
                      }
                      onChange={(event) =>
                        setScores((current) => ({
                          ...current,
                          [match.match_id]: {
                            ...current[match.match_id],
                            away_score: event.target.value,
                          },
                        }))
                      }
                    />
                    <Button
                      className="rounded-xl bg-[#102a27]"
                      onClick={() => submitScore(match)}
                      disabled={Boolean(pendingAction)}
                    >
                      {pendingAction === `score:${match.match_id}` && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save score
                    </Button>
                  </article>
                ))}
            </div>
          </section>
          )}
        </div>
        {activeTab === "divisions" && (
        <form
          onSubmit={saveDivision}
          className="h-fit rounded-[24px] border border-[#cfe4dc] bg-[#eef8f3] p-5 xl:sticky xl:top-6"
        >
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">
            Add division
          </div>
          <h3 className="mt-2 text-xl font-semibold text-[#102a27]">
            Define a competition group
          </h3>
          <div className="mt-5 grid gap-4">
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Division name
              <Input
                required
                value={division.name}
                onChange={(event) =>
                  setDivision((current) => ({
                    ...current,
                    name: event.target.value,
                  }))
                }
                className="bg-white"
                placeholder="U14 Girls Championship"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Classification
              <Input
                required
                value={division.classification}
                onChange={(event) =>
                  setDivision((current) => ({
                    ...current,
                    classification: event.target.value,
                  }))
                }
                className="bg-white"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Gender eligibility
              <select value={division.gender} onChange={(event) => setDivision((current) => ({ ...current, gender: event.target.value }))} className="h-10 rounded-md border border-[#d8e0d8] bg-white px-3">
                <option value="open">Open</option><option value="female">Girls / women</option><option value="male">Boys / men</option><option value="mixed">Mixed</option>
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Format
              <select
                value={division.format_template}
                onChange={(event) =>
                  setDivision((current) => ({
                    ...current,
                    format_template: event.target.value,
                  }))
                }
                className="h-10 rounded-md border border-[#d8e0d8] bg-white px-3"
              >
                {Object.entries(metadata.formats).map(([key, item]) => (
                  <option key={key} value={key}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Capacity
              <Input
                required
                type="number"
                min="2"
                max="1024"
                value={division.capacity}
                onChange={(event) =>
                  setDivision((current) => ({
                    ...current,
                    capacity: event.target.value,
                  }))
                }
                className="bg-white"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Entry fee (cents)
              <Input
                required
                type="number"
                min="0"
                max="10000000"
                value={division.entry_fee_cents}
                onChange={(event) =>
                  setDivision((current) => ({
                    ...current,
                    entry_fee_cents: event.target.value,
                  }))
                }
                className="bg-white"
              />
            </label>
            <label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">
              Required documents
              <Input
                value={division.required_documents}
                onChange={(event) =>
                  setDivision((current) => ({
                    ...current,
                    required_documents: event.target.value,
                  }))
                }
                className="bg-white"
                placeholder="Roster, insurance certificate"
              />
              <span className="text-xs font-normal text-[#60706a]">
                Separate document names with commas.
              </span>
            </label>
            <Button
              type="submit"
              disabled={saving}
              className="mt-2 rounded-xl bg-[#102a27]"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add
              division
            </Button>
          </div>
        </form>
        )}
        {activeTab === "fields" && (
        <form onSubmit={saveVenue} className="h-fit rounded-[24px] border border-[#cfe4dc] bg-[#eef8f3] p-5 xl:sticky xl:top-6"><div className="text-xs font-bold uppercase tracking-[0.18em] text-[#087f67]">Add field or court</div><div className="mt-5 grid gap-4"><label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">Venue<Input required value={venue.name} onChange={(event) => setVenue((current) => ({ ...current, name: event.target.value }))} placeholder="Central Sports Complex" className="bg-white" /></label><label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">Field / court name<Input required value={venue.resource_name} onChange={(event) => setVenue((current) => ({ ...current, resource_name: event.target.value }))} placeholder="Field 1" className="bg-white" /></label><label className="grid gap-1.5 text-sm font-semibold text-[#29443f]">Address<Input value={venue.address} onChange={(event) => setVenue((current) => ({ ...current, address: event.target.value }))} className="bg-white" /></label><Button type="submit" disabled={Boolean(pendingAction)} className="bg-[#102a27]">{pendingAction === "venue" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add field</Button></div></form>
        )}
      </div>
    </section>
  );
};

export default TournamentOperationsWorkspace;
